import { createContext, useContext, useState, useCallback } from "react";

const DICT = {
  en: {
    login: "Log in", have_invite: "I have an invite", open_app: "Open app",
    hero_badge: "Invite-only platform",
    hero_title_a: "All your music,", hero_title_b: "ad-free and audio-first.",
    hero_sub: "SONLYO turns music discovery into a pure listening experience: audio only, endless queues and your own library, installable on your phone.",
    hero_cta_in: "Enter with invite", hero_cta_open: "Open SONLYO", have_account: "I already have an account",
    f_adfree_t: "Ad-free", f_adfree_d: "Continuous playback. Nothing interrupts your music.",
    f_audio_t: "Audio-first", f_audio_d: "A clean, pure audio experience with no distractions.",
    f_radio_t: "Artist radio", f_radio_d: "The queue keeps going with more tracks from the artist.",
    f_lists_t: "Your playlists", f_lists_d: "Create, reorder and drag. Your library, your way.",
    f_pwa_t: "Installable", f_pwa_d: "Add it to your phone as an app (PWA), Android & iOS.",
    f_invite_t: "Invite-only", f_invite_d: "A curated community. Join with your code.",
    signin_title: "Log in", signin_sub: "Access your music.",
    signup_title: "Create account", signup_sub: "You need an invite code.",
    name: "Name", email: "Email", password: "Password", invite: "Invite code",
    enter: "Enter", register: "Sign up",
    q_have_invite: "Have an invite? ", q_have_account: "Already have an account? ",
    do_register: "Sign up", do_login: "Log in",
    side_title_a: "Music without limits.", side_title_b: "Ad-free.",
    side_sub: "An invite-only platform. Listen to whatever you want, whenever you want.",
    welcome_back: "Welcome back", account_created: "Account created",

    // nav
    nav_home: "Home", nav_search: "Search", nav_browse: "Browse",
    nav_artists: "Artists", nav_albums: "Albums", nav_liked: "Liked Songs",
    nav_library: "Your library", nav_settings: "Settings", logout: "Log out",
    create_playlist: "New playlist", first_playlist: "Create your first playlist with +",
    search_library_ph: "Search your library",

    // home
    greeting_morning: "Good morning", greeting_afternoon: "Good afternoon", greeting_evening: "Good evening",
    play_all: "Play all", artists: "Artists", albums: "Albums", songs: "Songs",
    recently_added: "Recently added", show_all: "Show all", tracks: "tracks", track_one: "track",
    catalog_empty_t: "The catalog is empty", catalog_empty_d: "Music will appear here once it's added.",
    go_search: "Search",
    made_for_you: "Made for you", recently_played: "Recently played",
    clear_history: "Clear", history_cleared: "History cleared",
    share: "Share", copied: "Link copied", crossfade: "Crossfade",
    sub_taste: "Based on your taste", sub_daily: "Your daily mix", sub_discover: "Discover", sub_start: "To get you started",
    lyrics: "Lyrics", no_lyrics: "No lyrics available for this track.",
    nav_profile: "Your profile",
    popular_tracks: "Popular tracks",
    shuffle: "Shuffle", repeat: "Repeat", like: "Like",
    thumb_up: "More like this", thumb_down: "Less like this",
    keep_awake: "Keep playing with the screen on",
    keep_awake_desc: "Stops the screen sleeping while SONLYO is open and playing, so the music is not cut off. It cannot keep playing if you switch apps or lock the phone. Uses more battery.",
    read_more: "Read more", read_less: "Read less",
    no_playlists_yet: "No playlists yet, create one below",
    rename: "Rename", delete: "Delete",
    collections: "Community collections",
    shared_playlists: "Shared playlists",
    playlist_public: "Public",
    playlist_private_state: "Private",
    playlist_shared: "Playlist shared with everyone",
    playlist_private: "Playlist is private again",
    collections_sub: "Uploads we could not match to an artist, grouped by channel.",
    complete_album: "Complete album", completing: "Completing",
    go_to_album: "Go to album",
    playlist_name_ph: "Playlist name", create: "Create", cancel: "Cancel",
    playlist_created: "Playlist created",
    avatar_change: "Change photo", avatar_remove: "Remove", avatar_updated: "Photo updated",
    avatar_removed: "Photo removed", avatar_too_big: "Keep the image under 2 MB",
    invite_email_ph: "Email address to invite", invite_send: "Send",
    invite_note_ph: "Private note (only admins see this)", invite_note: "Note",
    invite_sent: "Invitation sent to", invite_sent_label: "Sent",
    invite_link_copied: "No mail provider set up yet, invite link copied",
    invite_created: "Invite code created", invite_revoked: "Invite revoked", revoke: "Revoke",
    invite_open_mail: "Open in your mail app", invite_copy_link: "Copy invite link",
    request_title: "Missing something?", request_btn: "Request",
    request_placeholder: "Request an artist and we will add their music",
    request_working: "Adding", request_added: "tracks added from",
    request_queued: "on the way, their records are being added",
    request_already: "already on the way",
    request_nothing_new: "We already have everything we could find for that artist.",
    install_app: "Install app", install_title: "Install SONLYO",
    install_desc: "Add SONLYO to your device for a full-screen, app-like experience (Android & iOS).",
    install_unavailable: "Install from the browser menu.",
    install_add: "Add", install_banner_sub: "One tap to add it to your home screen.",
    chrome_sheet_sub: "Chrome only offers its install button once, so use the menu.",
    chrome_step_1: "Open the browser menu",
    chrome_step_2: "Choose Install app, or Add to Home screen",
    chrome_step_3: "Confirm. SONLYO appears with your apps.",
    ios_sheet_sub: "iPhone needs two taps, Apple gives no install button.",
    ios_use_safari: "Your browser may not offer this. If you do not see Add to Home Screen, open sonlyo.com in Safari.",
    ios_step_1: "Tap the share button in the toolbar",
    ios_step_2: "Scroll and choose Add to Home Screen",
    ios_step_3: "Tap Add. SONLYO appears on your home screen.",
    ios_note: "Audio pauses when you leave the app on a phone. YouTube stops playback in the background on mobile web, so it picks up again the moment you come back.",

    // library
    your_library: "Your library", nothing_here: "Nothing here.",
    ask_admin: "Ask admin to add music", track_removed: "Track removed",

    // browse / genres / moods
    browse: "Browse", browse_sub: "Explore by mood and genre.",
    moods: "Moods", genres: "Genres",
    all_artists: "All artists", all_albums: "All albums",
    filter_artists: "Filter artists...", filter_albums: "Filter albums...",
    no_artists: "No artists yet.", no_albums: "No albums yet.",
    no_genres: "No genres yet. An admin can run \"Fix artwork\" to tag the catalog.",
    genre: "Genre", mood: "Mood",
    mood_chill: "Chill", mood_party: "Party", mood_focus: "Focus",
    mood_workout: "Workout", mood_romance: "Romance", mood_sad: "Melancholy", mood_energy: "Energy",
    mood_lofi: "Lo-Fi", mood_latin: "Latin", mood_hiphop: "Hip-Hop", mood_throwback: "Throwback",
    mood_world: "World",

    // artist / album / playlist / liked
    artist: "Artist", album: "Album", playlist: "Playlist",
    follow: "Follow", following: "Following", unfollowed: "Unfollowed",
    radio: "Radio", radio_on: "Artist radio on",
    get_all_songs: "Get all songs", getting_songs: "Fetching...",
    artist_not_found: "Artist not found.", album_not_found: "Album not found.",
    playlist_not_found: "Playlist not found.",
    liked_songs: "Liked Songs", no_liked: "No liked songs yet. Tap the heart on any track.",
    playlist_empty: "Empty playlist. Add tracks from your library with +.",
    delete_playlist_confirm: "Delete this playlist?", playlist_deleted: "Playlist deleted",
    removed_from_playlist: "Removed from playlist",

    // global search
    no_results: "No results.",

    // track row menu
    play_next: "Play next", add_to_queue: "Add to queue", add_to_playlist: "Add to playlist",
    no_playlists: "No playlists yet.", added_to_playlist: "Added to playlist",
    will_play_next: "Will play next", added_to_queue: "Added to queue", could_not_add: "Could not add",

    // queue
    queue: "Queue", now_playing: "Now playing", up_next: "Up next · drag to reorder",
    visual: "Videoclip",
    track_unavailable: "This track is not available. Skipping.",
    radio_exhausted: "The radio has run out of tracks for this artist.",
    brave_title: "Keep playing in the background",
    brave_sub: "This browser stops the music when you leave the app. Brave has a setting that keeps it going.",
    brave_open: "Open in Brave",
    bg_ios_title: "The music stops when you leave",
    bg_ios_sub: "iPhone pauses playback outside the app, whichever browser you use. Holding the screen on keeps the session alive.",
    bg_ios_action: "Keep the screen on",
    queue_empty: "Nothing else in the queue.",

    // settings
    your_account: "Your account", profile: "Profile", change_password: "Change password",
    current_password: "Current password", new_password: "New password", confirm_password: "Confirm",
    save_changes: "Save changes", update_password: "Update password",
    profile_updated: "Profile updated", password_updated: "Password updated",
    passwords_no_match: "Passwords do not match",

    // autopilot
    autopilot_title: "Autopilot · catalog growth",
    autopilot_desc: "Automatically grows the catalog on a schedule, one seed per cycle.",
    every: "Every", minutes: "minutes", seeds_label: "Seeds (one per line)",
    save: "Save", saving: "Saving...", run_now: "Run now", running: "Running...",
    total_tracks: "Total tracks", autopilot_updated: "Autopilot updated",

    // artwork panel
    artwork_title: "Artwork · fix covers & genres",
    artwork_desc: "Fetch professional album covers, artist photos and genre tags for the whole catalog. Also merges duplicate albums.",
    fix_artwork: "Fix artwork", fixing: "Fixing...",
        artwork_done: "Artwork updated",
    db_records: "records used", db_cache: "of them cache",
    clear_cache: "Free up cache", clearing: "Freeing…",
    cache_cleared: "Rows freed",
    disco_title: "Import from discographies",
    disco_desc: "Reads an artist's real albums and track listings first, then looks for each song. Nothing that is not on a record gets in.",
    disco_placeholder: "Artist name",
    disco_add: "Queue",
    disco_queued: "Queued",
    disco_queued_count: "queued",
    disco_artists_done: "artists done",
    disco_albums_done: "albums done",
    disco_albums_left: "albums left",
    disco_missing: "not found",
    disco_run_now: "Run one step now",
    disco_queue_catalog: "Queue the artists we already have",
    short_albums_title: "Complete short albums",
    short_albums_desc: "Albums holding fewer than 6 tracks are treated as unfinished. A couple are completed every half hour, checked against the record’s real running order so only tracks that belong to it are imported.",
    short_albums_run_now: "Run one round now",
    short_albums_pending: "still short",
    short_albums_running: "Completing…",
    short_albums_left: "left",
    short_albums_done: "Albums completed",
    stop: "Stop",

    // presence
    listeners: "Listeners", listeners_desc: "Who is here now, when the rest were last around, and how long they have had an account. Only you can see this.",
    online_now: "Online", nobody_online: "Nobody is listening right now.",
    never_seen: "Never", just_now: "just now", joined: "joined",
    remove_user: "Delete account",
    remove_user_confirm: "Delete this account for good? Their playlists, favourites, follows and listening history go with it. This cannot be undone.",
    user_removed: "Account deleted",

    // invites
    invites: "Invites", invites_desc: "Generate codes to invite new users to SONLYO.",
    create_code: "Create code", no_codes: "No active codes.",
    available: "Available", used_by: "Used by",

    // admin import
    quick_import_t: "Quick import by artist or album",
    quick_import_d: "Enter an artist or album and bring all its tracks to the catalog at once.",
    as_album: "As album", album_name_opt: "Album name (optional)",
    import_all: "Import all", import_selected: "Import selected",
    search_tracks_ph: "Or search specific tracks...", search: "Search",
    album_name_ph: "Album name", search_add_hint: "Search and add music to the catalog.",
    catalog_search_ph: "Search the catalog...", no_matches: "No matches yet.",
    imported_n: "track(s) imported", added_n: "new track(s) added",
  },
  es: {
    login: "Iniciar sesión", have_invite: "Tengo invitación", open_app: "Abrir app",
    hero_badge: "Plataforma por invitación",
    hero_title_a: "Toda tu música,", hero_title_b: "sin anuncios y solo audio.",
    hero_sub: "SONLYO convierte el descubrimiento musical en una experiencia de escucha pura: solo audio, colas infinitas y tu propia biblioteca, instalable en tu móvil.",
    hero_cta_in: "Entrar con invitación", hero_cta_open: "Abrir SONLYO", have_account: "Ya tengo cuenta",
    f_adfree_t: "Sin anuncios", f_adfree_d: "Reproducción continua. Nada interrumpe tu música.",
    f_audio_t: "Solo audio", f_audio_d: "Una experiencia de audio limpia, sin distracciones.",
    f_radio_t: "Radio del artista", f_radio_d: "La cola sigue con más temas del artista.",
    f_lists_t: "Tus playlists", f_lists_d: "Crea, ordena y arrastra. Tu biblioteca, a tu manera.",
    f_pwa_t: "Instalable", f_pwa_d: "Añádela a tu móvil como app (PWA), Android e iOS.",
    f_invite_t: "Por invitación", f_invite_d: "Una comunidad cuidada. Entra con tu código.",
    signin_title: "Iniciar sesión", signin_sub: "Accede a tu música.",
    signup_title: "Crear cuenta", signup_sub: "Necesitas un código de invitación.",
    name: "Nombre", email: "Email", password: "Contraseña", invite: "Código de invitación",
    enter: "Entrar", register: "Registrarme",
    q_have_invite: "¿Tienes invitación? ", q_have_account: "¿Ya tienes cuenta? ",
    do_register: "Regístrate", do_login: "Inicia sesión",
    side_title_a: "Música sin límites.", side_title_b: "Sin anuncios.",
    side_sub: "Una plataforma por invitación. Escucha lo que quieras, cuando quieras.",
    welcome_back: "Bienvenido de nuevo", account_created: "Cuenta creada",

    // nav
    nav_home: "Inicio", nav_search: "Buscar", nav_browse: "Explorar",
    nav_artists: "Artistas", nav_albums: "Álbumes", nav_liked: "Me gusta",
    nav_library: "Tu biblioteca", nav_settings: "Ajustes", logout: "Cerrar sesión",
    create_playlist: "Nueva playlist", first_playlist: "Crea tu primera playlist con +",
    search_library_ph: "Busca en tu biblioteca",

    // home
    greeting_morning: "Buenos días", greeting_afternoon: "Buenas tardes", greeting_evening: "Buenas noches",
    play_all: "Reproducir todo", artists: "Artistas", albums: "Álbumes", songs: "Canciones",
    recently_added: "Añadido recientemente", show_all: "Ver todo", tracks: "temas", track_one: "tema",
    catalog_empty_t: "El catálogo está vacío", catalog_empty_d: "La música aparecerá aquí cuando se añada.",
    go_search: "Buscar",
    made_for_you: "Hecho para ti", recently_played: "Escuchado recientemente",
    clear_history: "Borrar", history_cleared: "Historial borrado",
    share: "Compartir", copied: "Enlace copiado", crossfade: "Transición (crossfade)",
    sub_taste: "Basado en tus gustos", sub_daily: "Tu mezcla diaria", sub_discover: "Descubre", sub_start: "Para empezar",
    lyrics: "Letra", no_lyrics: "No hay letra disponible para este tema.",
    nav_profile: "Tu perfil",
    popular_tracks: "Temas populares",
    shuffle: "Aleatorio", repeat: "Repetir", like: "Me gusta",
    thumb_up: "Más como esto", thumb_down: "Menos como esto",
    keep_awake: "Seguir sonando con la pantalla encendida",
    keep_awake_desc: "Evita que la pantalla se apague mientras SONLYO está abierto sonando, para que no se corte. No mantiene la música si cambias de app o bloqueas el móvil. Gasta más batería.",
    read_more: "Leer más", read_less: "Leer menos",
    no_playlists_yet: "Aún no tienes playlists, crea una abajo",
    rename: "Renombrar", delete: "Borrar",
    collections: "Colecciones de la comunidad",
    shared_playlists: "Listas compartidas",
    playlist_public: "Pública",
    playlist_private_state: "Privada",
    playlist_shared: "Lista compartida con todos",
    playlist_private: "La lista vuelve a ser privada",
    collections_sub: "Subidas que no hemos podido atribuir a un artista, agrupadas por canal.",
    complete_album: "Completar álbum", completing: "Completando",
    go_to_album: "Ir al álbum",
    playlist_name_ph: "Nombre de la playlist", create: "Crear", cancel: "Cancelar",
    playlist_created: "Playlist creada",
    avatar_change: "Cambiar foto", avatar_remove: "Quitar", avatar_updated: "Foto actualizada",
    avatar_removed: "Foto quitada", avatar_too_big: "La imagen debe pesar menos de 2 MB",
    invite_email_ph: "Email al que invitar", invite_send: "Enviar",
    invite_note_ph: "Nota privada (solo la ven los administradores)", invite_note: "Nota",
    invite_sent: "Invitacion enviada a", invite_sent_label: "Enviada",
    invite_link_copied: "Aun no hay proveedor de correo, enlace copiado",
    invite_created: "Codigo creado", invite_revoked: "Invitacion revocada", revoke: "Revocar",
    invite_open_mail: "Abrir en tu app de correo", invite_copy_link: "Copiar enlace de invitacion",
    request_title: "¿Falta algo?", request_btn: "Pedir",
    request_placeholder: "Pide un artista y añadimos su música",
    request_working: "Añadiendo", request_added: "temas añadidos de",
    request_queued: "en camino, estamos añadiendo sus discos",
    request_already: "ya está en camino",
    request_nothing_new: "Ya tenemos todo lo que hemos podido encontrar de ese artista.",
    install_app: "Instalar app", install_title: "Instalar SONLYO",
    install_desc: "Añade SONLYO a tu dispositivo para una experiencia a pantalla completa, como una app (Android e iOS).",
    install_unavailable: "Instala desde el menu del navegador.",
    install_add: "Añadir", install_banner_sub: "Un toque para añadirla a tu pantalla de inicio.",
    chrome_sheet_sub: "Chrome solo ofrece su boton de instalar una vez, asi que usa el menu.",
    chrome_step_1: "Abre el menu del navegador",
    chrome_step_2: "Elige Instalar app, o Anadir a pantalla de inicio",
    chrome_step_3: "Confirma. SONLYO aparece con tus apps.",
    ios_sheet_sub: "En iPhone son dos toques, Apple no da boton de instalar.",
    ios_use_safari: "Puede que tu navegador no lo ofrezca. Si no ves Anadir a pantalla de inicio, abre sonlyo.com en Safari.",
    ios_step_1: "Pulsa el boton de compartir de la barra",
    ios_step_2: "Baja y elige Anadir a pantalla de inicio",
    ios_step_3: "Pulsa Anadir. SONLYO aparece en tu pantalla de inicio.",
    ios_note: "El audio se pausa al salir de la app en el movil. YouTube detiene la reproduccion en segundo plano en web movil, asi que se reanuda en cuanto vuelves.",

    // library
    your_library: "Tu biblioteca", nothing_here: "No hay nada aquí.",
    ask_admin: "Pide al admin que añada música", track_removed: "Tema eliminado",

    // browse / genres / moods
    browse: "Explorar", browse_sub: "Explora por estado de ánimo y género.",
    moods: "Estados de ánimo", genres: "Géneros",
    all_artists: "Todos los artistas", all_albums: "Todos los álbumes",
    filter_artists: "Filtrar artistas...", filter_albums: "Filtrar álbumes...",
    no_artists: "Aún no hay artistas.", no_albums: "Aún no hay álbumes.",
    no_genres: "Aún no hay géneros. Un admin puede pulsar \"Arreglar carátulas\" para etiquetar el catálogo.",
    genre: "Género", mood: "Estado de ánimo",
    mood_chill: "Relax", mood_party: "Fiesta", mood_focus: "Concentración",
    mood_workout: "Entrenamiento", mood_romance: "Romance", mood_sad: "Melancolía", mood_energy: "Energía",
    mood_lofi: "Lo-Fi", mood_latin: "Latino", mood_hiphop: "Hip-Hop", mood_throwback: "Clásicos",
    mood_world: "Del mundo",

    // artist / album / playlist / liked
    artist: "Artista", album: "Álbum", playlist: "Playlist",
    follow: "Seguir", following: "Siguiendo", unfollowed: "Dejaste de seguir",
    radio: "Radio", radio_on: "Radio del artista activa",
    get_all_songs: "Traer todas las canciones", getting_songs: "Buscando...",
    artist_not_found: "Artista no encontrado.", album_not_found: "Álbum no encontrado.",
    playlist_not_found: "Playlist no encontrada.",
    liked_songs: "Me gusta", no_liked: "Aún no tienes temas favoritos. Pulsa el corazón en cualquier tema.",
    playlist_empty: "Playlist vacía. Añade temas desde tu biblioteca con +.",
    delete_playlist_confirm: "¿Eliminar esta playlist?", playlist_deleted: "Playlist eliminada",
    removed_from_playlist: "Quitado de la playlist",

    // global search
    no_results: "Sin resultados.",

    // track row menu
    play_next: "Reproducir a continuación", add_to_queue: "Añadir a la cola", add_to_playlist: "Añadir a playlist",
    no_playlists: "Aún no hay playlists.", added_to_playlist: "Añadido a la playlist",
    will_play_next: "Se reproducirá a continuación", added_to_queue: "Añadido a la cola", could_not_add: "No se pudo añadir",

    // queue
    queue: "Cola", now_playing: "Reproduciendo", up_next: "A continuación · arrastra para ordenar",
    visual: "Videoclip",
    track_unavailable: "Este tema no está disponible. Pasando al siguiente.",
    radio_exhausted: "La radio se ha quedado sin temas de este artista.",
    brave_title: "Sigue sonando en segundo plano",
    brave_sub: "Este navegador para la música al salir de la app. Brave tiene un ajuste que la mantiene.",
    brave_open: "Abrir en Brave",
    bg_ios_title: "La música se para al salir",
    bg_ios_sub: "El iPhone pausa la reproducción fuera de la app, uses el navegador que uses. Mantener la pantalla encendida conserva la sesión.",
    bg_ios_action: "Mantener la pantalla encendida",
    queue_empty: "No hay nada más en la cola.",

    // settings
    your_account: "Tu cuenta", profile: "Perfil", change_password: "Cambiar contraseña",
    current_password: "Contraseña actual", new_password: "Nueva contraseña", confirm_password: "Confirmar",
    save_changes: "Guardar cambios", update_password: "Actualizar contraseña",
    profile_updated: "Perfil actualizado", password_updated: "Contraseña actualizada",
    passwords_no_match: "Las contraseñas no coinciden",

    // autopilot
    autopilot_title: "Autopiloto · crecimiento del catálogo",
    autopilot_desc: "Hace crecer el catálogo automáticamente, una semilla por ciclo.",
    every: "Cada", minutes: "minutos", seeds_label: "Semillas (una por línea)",
    save: "Guardar", saving: "Guardando...", run_now: "Ejecutar ahora", running: "Ejecutando...",
    total_tracks: "Temas totales", autopilot_updated: "Autopiloto actualizado",

    // artwork panel
    artwork_title: "Carátulas · arreglar imágenes y géneros",
    artwork_desc: "Trae carátulas de álbum, fotos de artista y géneros profesionales para todo el catálogo. También fusiona álbumes duplicados.",
    fix_artwork: "Arreglar carátulas", fixing: "Arreglando...",
        artwork_done: "Carátulas actualizadas",
    db_records: "registros usados", db_cache: "de ellos caché",
    clear_cache: "Liberar caché", clearing: "Liberando…",
    cache_cleared: "Registros liberados",
    disco_title: "Importar por discografía",
    disco_desc: "Lee primero los discos reales del artista y sus listas de cortes, y solo después busca cada canción. No entra nada que no esté en un disco.",
    disco_placeholder: "Nombre del artista",
    disco_add: "Encolar",
    disco_queued: "En cola",
    disco_queued_count: "en cola",
    disco_artists_done: "artistas hechos",
    disco_albums_done: "discos hechos",
    disco_albums_left: "discos restantes",
    disco_missing: "sin encontrar",
    disco_run_now: "Dar un paso ahora",
    disco_queue_catalog: "Encolar los artistas que ya tenemos",
    short_albums_title: "Completar álbumes incompletos",
    short_albums_desc: "Se consideran incompletos los álbumes con menos de 6 canciones. Se completan un par cada media hora, contrastados con la lista real de cortes del disco para importar solo lo que pertenece a él.",
    short_albums_run_now: "Hacer una ronda ahora",
    short_albums_pending: "aún cortos",
    short_albums_running: "Completando…",
    short_albums_left: "quedan",
    short_albums_done: "Álbumes completados",
    stop: "Parar",

    // presence
    listeners: "Oyentes", listeners_desc: "Quién está ahora, cuándo se vio al resto por última vez y cuánto hace que tienen cuenta. Solo tú ves esto.",
    online_now: "En línea", nobody_online: "Ahora mismo no hay nadie escuchando.",
    never_seen: "Nunca", just_now: "ahora mismo", joined: "se unió",
    remove_user: "Borrar cuenta",
    remove_user_confirm: "¿Borrar esta cuenta definitivamente? Se van con ella sus playlists, favoritos, seguidos e historial. No se puede deshacer.",
    user_removed: "Cuenta borrada",

    // invites
    invites: "Invitaciones", invites_desc: "Genera códigos para invitar a nuevos usuarios a SONLYO.",
    create_code: "Crear código", no_codes: "No hay códigos activos.",
    available: "Disponible", used_by: "Usado por",

    // admin import
    quick_import_t: "Importación rápida por artista o álbum",
    quick_import_d: "Introduce un artista o álbum y trae todos sus temas al catálogo de una vez.",
    as_album: "Como álbum", album_name_opt: "Nombre del álbum (opcional)",
    import_all: "Importar todo", import_selected: "Importar seleccionados",
    search_tracks_ph: "O busca temas concretos...", search: "Buscar",
    album_name_ph: "Nombre del álbum", search_add_hint: "Busca y añade música al catálogo.",
    catalog_search_ph: "Busca en el catálogo...", no_matches: "Sin coincidencias todavía.",
    imported_n: "tema(s) importado(s)", added_n: "tema(s) nuevo(s) añadido(s)",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const initial = localStorage.getItem("sonlyo_lang")
    || ((navigator.language || "en").toLowerCase().startsWith("es") ? "es" : "en");
  const [lang, setLangState] = useState(initial === "es" ? "es" : "en");
  const setLang = useCallback((l) => { localStorage.setItem("sonlyo_lang", l); setLangState(l); }, []);
  const toggle = useCallback(() => setLang(lang === "en" ? "es" : "en"), [lang, setLang]);
  const t = useCallback((k) => (DICT[lang] && DICT[lang][k]) || DICT.en[k] || k, [lang]);
  return <I18nContext.Provider value={{ lang, setLang, toggle, t }}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

export function LanguageToggle({ className = "" }) {
  const { lang, toggle } = useI18n();
  return (
    <button data-testid="lang-toggle" onClick={toggle}
      className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border border-white/15 text-zinc-300 hover:text-white hover:border-white/40 transition-colors ${className}`}>
      {lang === "en" ? "ES" : "EN"}
    </button>
  );
}
